import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { AuthContext, type AuthState } from './auth-context';

/**
 * Holds the OIDC access token (in sessionStorage, not localStorage) and the
 * workspace id, exposes login/logout, and performs a silent cookie-based refresh
 * (`POST /auth/refresh`) when the access token expires. The refresh token itself
 * lives only in an HttpOnly cookie, so it is never readable by script.
 */

const ACCESS_TOKEN_KEY = 'chessreps.accessToken';
const WORKSPACE_KEY = 'chessreps.workspaceId';
const REFRESH_LOCK = 'chessreps.refresh';

/**
 * Run `task` while holding a cross-tab lock where the Web Locks API exists. Refresh
 * tokens rotate on every use and the server revokes the whole family when a rotated
 * token is presented again, so two tabs must never refresh with the same cookie.
 */
function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? locks.request(REFRESH_LOCK, task) : task();
}

function storageGet(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Ignore: in-memory state still drives this session.
  }
}

function storageRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

export function AuthProvider({
  apiOrigin,
  children,
}: {
  apiOrigin: string;
  children: ReactNode;
}): JSX.Element {
  const [token, setToken] = useState<string | null>(() => storageGet(ACCESS_TOKEN_KEY));
  const [workspaceId, setWorkspaceId] = useState<string | null>(() => storageGet(WORKSPACE_KEY));

  const login = useCallback((accessToken: string, _expiresIn: number, ws: string) => {
    storageSet(ACCESS_TOKEN_KEY, accessToken);
    storageSet(WORKSPACE_KEY, ws);
    setToken(accessToken);
    setWorkspaceId(ws);
  }, []);

  const logout = useCallback(() => {
    void fetch(`${apiOrigin}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(
      () => {},
    );
    storageRemove(ACCESS_TOKEN_KEY);
    storageRemove(WORKSPACE_KEY);
    setToken(null);
    setWorkspaceId(null);
  }, [apiOrigin]);

  // Concurrent 401s share one refresh: a second rotation with the same cookie would
  // look like token theft to the server and revoke the session.
  const inflightRefresh = useRef<Promise<string | null> | null>(null);
  const refresh = useCallback((): Promise<string | null> => {
    if (inflightRefresh.current) return inflightRefresh.current;
    const run = withRefreshLock(async (): Promise<string | null> => {
      try {
        const resp = await fetch(`${apiOrigin}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (!resp.ok) return null;
        const data = (await resp.json()) as { accessToken?: string };
        if (!data.accessToken) return null;
        storageSet(ACCESS_TOKEN_KEY, data.accessToken);
        setToken(data.accessToken);
        return data.accessToken;
      } catch {
        return null;
      }
    })
      .catch(() => null)
      .finally(() => {
        inflightRefresh.current = null;
      });
    inflightRefresh.current = run;
    return run;
  }, [apiOrigin]);

  const value = useMemo<AuthState>(
    () => ({
      token,
      workspaceId,
      isAuthenticated: token !== null && token !== '',
      login,
      logout,
      refresh,
    }),
    [token, workspaceId, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
